// Lib
import React, { useMemo } from 'react';

// Hooks
import { useTranslator } from '../../../../components/i18n/useTranslator';

// Selectors
import { getDeleteTasksSuggestionDismissed, getTitleSuggestionAck } from '../../taskListSelector';

// Components
import FeatureSuggestions from '../../../suggestion/FeatureSuggestions';
import SingleEditorDeleteTasksSuggestion from './SingleEditorDeleteTasksSuggestion';
import SingleEditorTitleSuggestion from './SingleEditorTitleSuggestion';

// Constants
import { ELEMENT_DEFAULT_WIDTH } from '../../../../../common/elements/elementConstants';
import { FEATURE_SUGGESTIONS } from '../../../suggestion/suggestionConstants';

// Types
import type { ImMNElement } from '../../../../../common/elements/elementModelTypes';

const SharedFeatureSuggestions = FeatureSuggestions as React.ComponentType<Record<string, unknown>>;

// Whether the list currently has a title, or has completed tasks, is decided by the live editor
// document (see useTaskListFeatureSuggestionTriggers), so all that's left to check is whether this
// list has already had its say about the feature.
const shouldShowTitleSuggestion = (element: ImMNElement): boolean => !getTitleSuggestionAck(element);
const shouldShowDeleteTasksSuggestion = (element: ImMNElement): boolean =>
    !getDeleteTasksSuggestionDismissed(element);

type SingleEditorTaskListFeatureSuggestionsProps = {
    element: ImMNElement;
    isEditable: boolean;
    isFocusedForegroundElement?: boolean;
};

/**
 * Renders the feature suggestions offered by a single-editor task list.
 *
 * Which suggestions are currently live is tracked as per-element local data by
 * useTaskListFeatureSuggestionTriggers.
 */
const SingleEditorTaskListFeatureSuggestions: React.FC<SingleEditorTaskListFeatureSuggestionsProps> = ({
    element,
    isEditable,
    isFocusedForegroundElement,
}) => {
    const translate = useTranslator('elements', 'elementTypes.taskList.featureSuggestions');
    const addTitleQuestion = translate('title.question');
    const deleteTasksQuestion = translate('deleteTasks.question');

    const possibleSuggestions = useMemo(
        () => [
            {
                feature: FEATURE_SUGGESTIONS.TITLE,
                shouldShow: shouldShowTitleSuggestion,
                question: addTitleQuestion,
                component: SingleEditorTitleSuggestion,
            },
            {
                feature: FEATURE_SUGGESTIONS.DELETE_COMPLETED_TASKS,
                shouldShow: shouldShowDeleteTasksSuggestion,
                question: deleteTasksQuestion,
                component: SingleEditorDeleteTasksSuggestion,
            },
        ],
        [addTitleQuestion, deleteTasksQuestion],
    );

    return (
        <SharedFeatureSuggestions
            fullWidth
            element={element}
            isEditable={isEditable}
            isFocusedForegroundElement={isFocusedForegroundElement}
            cardWidth={ELEMENT_DEFAULT_WIDTH}
            possibleSuggestions={possibleSuggestions}
        />
    );
};

export default SingleEditorTaskListFeatureSuggestions;
